import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner, Card } from 'react-bootstrap';
import Flex from 'components/common/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { uploadMultipleAndMap } from 'services/files/fileService';
import { toast } from 'react-toastify';
import Dropzone from 'react-dropzone';

const BookingInPhotoStep = ({ 
  title, 
  grnNo, 
  recordId,
  app, 
  onNext, 
  s3Prefix,
  isOptional = false,
  skipText = "Skip",
  nextText = "Next Step"
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isStartingCamera, setIsStartingCamera] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera is not supported on this browser. Please use file upload instead.');
      return;
    }

    setIsStartingCamera(true);
    setCameraError('');

    try {
      stopCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment'
        },
        audio: false
      });

      streamRef.current = stream;
      setIsCameraOpen(true);
    } catch (error) {
      console.error('Camera error:', error);
      setCameraError('Unable to access camera. Check browser permissions or use file upload instead.');
      setIsCameraOpen(false);
    } finally {
      setIsStartingCamera(false);
    }
  };

  useEffect(() => {
    if (!isCameraOpen || !videoRef.current || !streamRef.current) return;

    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play().catch(err => {
      console.error('Video play error:', err);
      setCameraError('Unable to start live camera preview.');
    });
  }, [isCameraOpen]);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const onDrop = (acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    }));
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    URL.revokeObjectURL(newFiles[index].preview);
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      toast.warning('Camera is not ready yet');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(blob => {
      if (!blob) {
        toast.error('Failed to capture photo');
        return;
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const file = new File([blob], `booking-in-${timestamp}.jpg`, {
        type: 'image/jpeg'
      });

      file.preview = URL.createObjectURL(file);
      setFiles(prev => [...prev, file]);
      toast.success('Photo captured');
    }, 'image/jpeg', 0.92);
  };

  const processUpload = async () => {
    if (files.length === 0) {
      if (isOptional) {
        onNext();
      } else {
        toast.warning('Please capture or select at least one photo');
      }
      return;
    }

    setIsUploading(true);
    try {
      const res = await uploadMultipleAndMap({
        files,
        bucketName: 'eva-files',
        app,
        prefix: s3Prefix,
        recordId,
        recordValue: grnNo
      });

      if (res.data?.success) {
        toast.success('Photos uploaded and mapped successfully');
        onNext();
      } else {
        throw new Error(res.data?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload photos');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="booking-in-photo-step py-2">
      <div className="text-center mb-4">
        <h3 className="fw-bold text-primary">{title}</h3>
        <p className="text-500 fs--1">Use camera capture or file upload. Multiple images are supported.</p>
      </div>

      <Card className="border-0 shadow-sm mb-4 overflow-hidden" style={{ borderRadius: '15px', background: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(10px)' }}>
        <Card.Body className="p-0">
          <Dropzone onDrop={onDrop} accept="image/*">
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                {...getRootProps({
                  onClick: e => {
                    e.preventDefault();
                    startCamera();
                  }
                })}
                className={`p-5 text-center cursor-pointer transition-base ${isDragActive ? 'bg-primary-soft' : ''}`}
                style={{
                  border: '2px dashed #d8e2ef',
                  borderRadius: '15px',
                  margin: '15px',
                  backgroundColor: isDragActive ? 'rgba(44, 123, 229, 0.05)' : 'transparent'
                }}
              >
                <input
                  {...getInputProps({
                    noClick: true
                  })}
                />
                <div className="avatar avatar-4xl mb-3 mx-auto">
                  <div className="avatar-name rounded-circle bg-soft-primary text-primary d-flex align-items-center justify-content-center">
                    <FontAwesomeIcon icon="camera" size="lg" />
                  </div>
                </div>
                <h5 className="text-700">
                  {isStartingCamera ? 'Opening Camera...' : 'Tap to Take Photo'}
                </h5>
                <p className="fs--1 text-500 mb-2">or drag and drop images here</p>
                <Button
                  variant="link"
                  className="p-0 fs--1"
                  onClick={e => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Choose existing photos instead
                </Button>
              </div>
            )}
          </Dropzone>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={e => {
              const acceptedFiles = Array.from(e.target.files || []);
              if (acceptedFiles.length > 0) {
                onDrop(acceptedFiles);
              }
              e.target.value = '';
            }}
          />

          {cameraError && (
            <div className="px-3 pb-3">
              <div className="alert alert-warning mb-0">{cameraError}</div>
            </div>
          )}

          {files.length > 0 && (
            <div className="p-3 border-top bg-light">
              <div className="row g-3">
                {files.map((file, index) => (
                  <div key={index} className="col-4 col-md-3 col-lg-2">
                    <div className="position-relative group">
                      <img
                        src={file.preview}
                        alt="preview"
                        className="img-fluid rounded-3 shadow-sm border"
                        style={{ height: '100px', width: '100%', objectFit: 'cover' }}
                      />
                      <Button
                        variant="link"
                        className="position-absolute top-0 end-0 p-1 text-danger bg-white rounded-circle shadow-sm m-1"
                        style={{ width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                      >
                        <FontAwesomeIcon icon="times" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <Flex justifyContent="between" alignItems="center">
        {isOptional ? (
          <Button variant="link" className="text-600 px-0" onClick={onNext} disabled={isUploading}>
            {skipText}
          </Button>
        ) : <div />}
        
        <Button 
          variant="primary" 
          className="px-5 shadow-sm rounded-pill py-2 fw-semi-bold"
          onClick={processUpload}
          disabled={isUploading || (!isOptional && files.length === 0)}
        >
          {isUploading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Uploading...
            </>
          ) : (
            <>
              {nextText} <FontAwesomeIcon icon="chevron-right" className="ms-2 fs--2" />
            </>
          )}
        </Button>
      </Flex>

      {isCameraOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999999,
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              position: 'relative',
              width: '100%',
              maxWidth: '720px',
              overflow: 'hidden'
            }}
          >
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Take Photo</h5>
              <Button
                variant="link"
                className="text-secondary p-0"
                onClick={() => {
                  stopCamera();
                  setIsCameraOpen(false);
                }}
              >
                Close
              </Button>
            </div>

            <div className="p-3 bg-dark">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-100 rounded"
                style={{ maxHeight: '480px', objectFit: 'cover' }}
              />
            </div>

            <div className="p-3 border-top d-flex justify-content-center gap-2">
              <Button variant="success" onClick={capturePhoto}>
                Capture Photo
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  stopCamera();
                  setIsCameraOpen(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

BookingInPhotoStep.propTypes = {
  title: PropTypes.string.isRequired,
  grnNo: PropTypes.string.isRequired,
  recordId: PropTypes.number,
  app: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired,
  s3Prefix: PropTypes.string.isRequired,
  isOptional: PropTypes.bool,
  skipText: PropTypes.string,
  nextText: PropTypes.string
};

export default BookingInPhotoStep;
